import { getApiBaseUrl, isDevelopment } from "@utils/constants";

/**
 * Default request timeout in milliseconds
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Default headers for API requests
 */
const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

/**
 * Auth endpoints excluded from the 401 interceptor to prevent refresh loops
 */
const AUTH_BYPASS_ENDPOINTS = ["/auth/login", "/auth/logout", "/auth/refresh"];

/**
 * Module-level state for the 401 refresh interceptor.
 * refreshPromise acts as a mutex to ensure only one refresh attempt runs at a time.
 */
let refreshPromise = null;
let sessionExpiredCallback = null;

/**
 * Register a callback to be invoked when the session is irrecoverably expired.
 * Called by AuthContext to wire the logout function into the API layer.
 * @param {Function} callback - Function to call on session expiry (typically logout)
 */
export const setSessionExpiredCallback = (callback) => {
  sessionExpiredCallback = callback;
};

/**
 * Get API configuration object
 * @returns {Object} API configuration
 */
export const getApiConfig = () => ({
  baseURL: getApiBaseUrl(),
  timeout: DEFAULT_TIMEOUT,
  headers: DEFAULT_HEADERS,
});

/**
 * Attempt to refresh the session token.
 * Uses a mutex pattern so concurrent 401s trigger only one refresh.
 * @returns {Promise<boolean>} Whether the refresh succeeded
 */
const attemptTokenRefresh = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const { authApi } = await import("@services/authApi");
      await authApi.refreshSession();
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Execute a single fetch call with timeout and abort support
 * @param {string} url - Full request URL
 * @param {Object} fetchOptions - Fetch configuration
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>} Raw fetch Response
 */
const executeFetch = async (url, fetchOptions, timeout) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Generic fetch wrapper with error handling, retries, and 401 interception.
 * On a 401 response, attempts a silent token refresh and replays the request once.
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @param {number} retries - Number of retry attempts
 * @returns {Promise<Object>} API response data
 */
export const apiRequest = async (endpoint, options = {}, retries = 3) => {
  const config = getApiConfig();
  const url = `${config.baseURL}${endpoint}`;
  const shouldInterceptAuth = !AUTH_BYPASS_ENDPOINTS.some((path) =>
    endpoint.includes(path)
  );

  const fetchOptions = {
    timeout: config.timeout,
    credentials: "include",
    headers: {
      ...config.headers,
      ...options.headers,
    },
    ...options,
  };

  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await executeFetch(url, fetchOptions, config.timeout);

      if (response.status === 401 && shouldInterceptAuth) {
        const refreshed = await attemptTokenRefresh();

        if (refreshed) {
          const retryResponse = await executeFetch(
            url,
            fetchOptions,
            config.timeout
          );

          if (retryResponse.ok) {
            return await retryResponse.json();
          }
        }

        if (sessionExpiredCallback) {
          sessionExpiredCallback();
        }
        throw new Error("HTTP 401: Session expired");
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      lastError = error;

      if (error.message?.includes("HTTP 401")) {
        throw error;
      }

      if (isDevelopment()) {
        console.warn(
          `API Request failed (attempt ${attempt + 1}/${retries + 1}):`,
          error.message
        );
      }

      if (attempt === retries) break;

      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, attempt) * 1000)
      );
    }
  }

  console.error(
    `API Request failed after ${retries + 1} attempts:`,
    lastError?.message || lastError?.toString() || "Unknown error"
  );
  throw lastError;
};

/**
 * GET request helper
 * @param {string} endpoint - API endpoint path
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} API response data
 */
export const apiGet = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;

  return apiRequest(url, { method: "GET" });
};

/**
 * POST request helper
 * @param {string} endpoint - API endpoint path
 * @param {Object} data - Request body data
 * @returns {Promise<Object>} API response data
 */
export const apiPost = async (endpoint, data = {}) => {
  return apiRequest(endpoint, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

/**
 * PUT request helper
 * @param {string} endpoint - API endpoint path
 * @param {Object} data - Request body data
 * @returns {Promise<Object>} API response data
 */
export const apiPut = async (endpoint, data = {}) => {
  return apiRequest(endpoint, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request helper
 * @param {string} endpoint - API endpoint path
 * @returns {Promise<Object>} API response data
 */
export const apiDelete = async (endpoint) => {
  return apiRequest(endpoint, { method: "DELETE" });
};

/**
 * Handle API errors consistently across the application
 * @param {Error} error - The error to handle
 * @param {string} context - Context where the error occurred
 * @throws {Error} Processed error with user-friendly message
 */
export const handleApiError = (error, context = "API request") => {
  if (isDevelopment()) {
    console.error(
      `API Error in ${context}:`,
      error?.message || error?.toString() || "Unknown error"
    );
  }

  if (error.name === "AbortError") {
    throw new Error("Request timeout - please try again");
  }

  if (error.message.includes("Failed to fetch")) {
    throw new Error("Network error - please check your connection");
  }

  if (error.message.includes("HTTP 404")) {
    throw new Error("Data not found");
  }

  if (error.message.includes("HTTP 500")) {
    throw new Error("Server error - please try again later");
  }

  throw new Error(error.message || "An unexpected error occurred");
};
